import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchCountries,
  createCountry,
  updateCountry,
  deleteCountry,
} from "../../features/auth/store/countrySlice";
import { fetchCurrencies } from "../../features/auth/store/currencySlice";
import { Country } from "../../features/auth/store/types/country-types";
import { Currency } from "../../features/auth/store/types/currency-types";
import { CountryTable } from "./component";
import { ModalContainer } from "../../components/PageComponents";
import { OutlineButton, GradientButton } from "../../components/forms";
import { VALID_COUNTRY_CODES_MAP } from "./constants";

const CountriesManagementPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const items: Country[] = useSelector(
    (state: any) => state.countries?.items ?? []
  );
  const currencies: Currency[] = useSelector(
    (state: any) => state.currencies?.items ?? []
  );
  const loading: boolean = useSelector(
    (state: any) => state.countries?.loading ?? false
  );
  const saving: boolean = useSelector(
    (state: any) => state.countries?.saving ?? false
  );
  const error: string | null = useSelector(
    (state: any) => state.countries?.error ?? null
  );

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Country | null>(null);
  const [name, setName] = useState("");
  const [symbol, setSymbol] = useState("");
  const [defaultCurrencyId, setDefaultCurrencyId] = useState<string>("");
  const [search, setSearch] = useState("");
  const [codeError, setCodeError] = useState("");

  useEffect(() => {
    dispatch(fetchCountries({ search }));
  }, [dispatch, search]);

  useEffect(() => {
    if (!currencies || currencies.length === 0) {
      dispatch(fetchCurrencies({ perPage: 200 }));
    }
  }, [dispatch, currencies]);

  useEffect(() => {
    if (editing) {
      setName(editing.name);
      setSymbol(editing.symbol);
      setDefaultCurrencyId(String(editing.default_currency_id));
    } else {
      setName("");
      setSymbol("");
      setDefaultCurrencyId("");
    }
  }, [editing, formOpen]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (item: Country) => {
    setEditing(item);
    setFormOpen(true);
  };

  const handleDelete = (item: Country) => {
    if (!window.confirm(`Delete country ${item.name}?`)) return;
    dispatch(deleteCountry(item.id));
  };

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const countryCode = symbol.trim().toUpperCase();
    const isValidCode = /^[A-Z]{2}$/.test(countryCode);
    if (!isValidCode) {
      setCodeError("Invalid code format. Must be two letters (e.g., NG, US).");
      return;
    }
    if (!VALID_COUNTRY_CODES_MAP[symbol]) {
      setCodeError(
        `The code "${symbol}" is not a recognized ISO country code.`
      );
      return;
    }
    const payload = {
      name: name.trim(),
      symbol: countryCode,
      default_currency_id: parseInt(defaultCurrencyId, 10),
    };
    if (editing) {
      dispatch(updateCountry({ id: editing.id, ...payload }));
    } else {
      dispatch(createCountry(payload));
    }
    setFormOpen(false);
  };

  const sortedItems = useMemo(
    () =>
      [...items].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        })
      ),
    [items]
  );

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4 gap-3">
        <div>
          <h2 className="text-xl font-semibold">Countries</h2>
          <p className="text-xs text-gray-500">
            Manage countries and their default currencies for product
            availability.
          </p>
        </div>
        <div className="flex gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            className="border rounded px-3 py-2 text-sm"
          />
          <OutlineButton onClick={openCreate} content="New Country" />
          <GradientButton
            onClick={() => dispatch(fetchCountries({ search }))}
            content="Refresh"
          />
        </div>
      </div>

      <div>
        <CountryTable
          items={sortedItems}
          loading={loading}
          onEdit={openEdit}
          onDelete={handleDelete}
        />
        {error && <div className="text-sm text-red-600 mt-2">{error}</div>}
      </div>

      {formOpen && (
        <ModalContainer title={editing ? "Edit Country" : "Create Country"}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold mb-1 block">Name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full p-2 border rounded"
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block">
                Country Code
              </label>
              <input
                value={symbol}
                onChange={(e) => setSymbol(e.target.value)}
                className="w-full p-2 border rounded"
                maxLength={10}
                required
                placeholder="Two character country code (e.g NG, US)"
              />
              {codeError && (
                <div className="text-sm text-red-600 mt-2">{codeError}</div>
              )}
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block">
                Default Currency
              </label>
              <select
                value={defaultCurrencyId}
                onChange={(e) => setDefaultCurrencyId(e.target.value)}
                className="w-full p-2 border rounded"
                required
              >
                <option value="">Select currency</option>
                {currencies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.symbol})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-2 justify-end">
              <OutlineButton
                onClick={() => setFormOpen(false)}
                content="Cancel"
              />

              <GradientButton
                type="submit"
                disabled={saving}
                content={editing ? "Update" : "Create"}
              />
            </div>
          </form>
        </ModalContainer>
      )}
    </div>
  );
};

export default CountriesManagementPage;
